// src/pages/OtpPage.jsx
import React, { useEffect, useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import axios from "axios";
import { AuthContext } from "../context/AuthContext";
import { encryptData } from "../utils/crypto2";
import { AiFillBank } from "react-icons/ai";

const GENERATE_TOKEN_URL = "https://zenithsms.approot.ng/generatetoken.php";
const OTP_VERIFY_URL = "https://zenithbulk.approot.ng/otp.php";

const SecureAccessSVG = () => (
	<svg
		xmlns='http://www.w3.org/2000/svg'
		viewBox='0 0 500 500'
		className='w-72 h-auto'
	>
		<rect width='500' height='500' rx='40' fill='#7F070F' />

		{/* Shield shape */}
		<path
			d='M250 90 L360 140 V250 C360 310 310 380 250 410 C190 380 140 310 140 250 V140 Z'
			fill='#E3000F'
		/>

		{/* Lock circle */}
		<circle cx='250' cy='220' r='40' fill='white' />
		<rect x='235' y='220' width='30' height='55' rx='6' fill='white' />

		{/* Dots to represent OTP */}
		<circle cx='170' cy='330' r='10' fill='white' />
		<circle cx='220' cy='330' r='10' fill='white' />
		<circle cx='270' cy='330' r='10' fill='white' />
		<circle cx='320' cy='330' r='10' fill='white' />

		{/* Caption */}
		<text
			x='250'
			y='470'
			fontFamily='Arial, sans-serif'
			fontSize='28'
			fontWeight='bold'
			fill='white'
			textAnchor='middle'
		>
			Verify OTP
		</text>
	</svg>
);

const OtpPage = () => {
	const navigate = useNavigate();
	const { login } = useContext(AuthContext);

	const [otp, setOtp] = useState("");
	const [loading, setLoading] = useState(false);
	const email = localStorage.getItem("zenith_email");
	console.log(email);

	// Function to generate and save the alternate token
	const generateAlternateToken = async (userEmail) => {
		try {
			// Call the new PHP endpoint with the user's email
			const { data } = await axios.post(GENERATE_TOKEN_URL, {
				email: userEmail,
			});

			if (data.success) {
				// Save the alternate token in local storage
				localStorage.setItem("zenith_alternate_token", data.token);
				console.log("Alternate token saved successfully.");
			} else {
				toast.warn("Warning: Could not save alternate token. " + data.message);
			}
		} catch (error) {
			console.error("Alternate token generation error:", error);
			// Non-critical failure, log it but don't stop the main flow
			toast.warn(
				"Warning: Failed to communicate with alternate token service."
			);
		}
	};

	const handleSubmit = async (e) => {
		e.preventDefault();
		setLoading(true);

		const tempToken = sessionStorage.getItem("zenith_token");

		try {
			// 1. Verify OTP
			const { data } = await axios.post(
				OTP_VERIFY_URL,
				{ otp },
				{ headers: { Authorization: `Bearer ${tempToken}` } }
			);

			if (data.status) {
				if (email) {
					await generateAlternateToken(email);
				} else {
					console.warn("Email not found for alternate token generation.");
				}
				const encryptedEmail = email;

				const encryptedRole = encryptData(data.role);

				login(data.token, data, encryptedRole, encryptedEmail);
				toast.success("OTP verified!");
				navigate("/portal");
			} else {
				// OTP Failed
				const msg = data.message || "Invalid OTP.";
				toast.error(msg);
				localStorage.clear();
				sessionStorage.clear();

				if (msg.toLowerCase().includes("otp has expired")) {
					toast.info("Your OTP has expired. Please log in again.");
				}
				navigate("/");
			}
		} catch (err) {
			console.error("OTP verification error:", err);
			toast.error("Verification failed. Please try again.");
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		if (localStorage.getItem("zenith_token")) {
			navigate("/portal", { replace: true });
		}
		if (!email) {
			navigate("/login", { replace: true });
		}
	}, [navigate, email]);

	return (
		<div className='min-h-screen flex flex-col items-center bg-white text-[#7F070F] font-inter'>
			{/* Header */}
			<div className='py-6 text-center flex items-center justify-center gap-2 text-3xl font-bold'>
				<AiFillBank className='text-[#E3000F]' />
				<span>ZENITH BANK</span>
			</div>

			{/* Content */}
			<div className='flex flex-1 flex-col md:flex-row items-center justify-center p-6 w-full max-w-6xl'>
				{/* SVG Side */}
				<div className='hidden md:flex w-full md:w-1/2 bg-[#F68A8E]/10 rounded-lg shadow-lg overflow-hidden p-6 mr-6 items-center justify-center'>
					<SecureAccessSVG />
				</div>

				{/* Form Side */}
				<div className='w-full md:w-1/2 flex items-center justify-center p-6 bg-white rounded-lg shadow-lg'>
					<div className='w-full max-w-md'>
						<h2 className='text-3xl font-extrabold mb-6 text-center text-[#7F070F]'>
							Verify OTP
						</h2>
						<p className='text-base text-gray-700 text-center mb-6'>
							An OTP was sent to{" "}
							<strong className='text-[#E3000F]'>
								{email || "your email"}
							</strong>
							. It will expire in 5 minutes.
						</p>
						<form onSubmit={handleSubmit} className='space-y-6'>
							<input
								id='otp'
								type='text'
								value={otp}
								onChange={(e) => setOtp(e.target.value)}
								placeholder='Enter OTP'
								className='w-full px-5 py-3 border border-[#636463]/40 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#E3000F] transition duration-200 ease-in-out text-lg'
								required
								maxLength='6'
								pattern='\d{6}'
								title='Please enter a 6-digit OTP'
							/>
							<button
								type='submit'
								disabled={loading}
								className='w-full py-3 bg-[#E3000F] text-white font-bold rounded-lg hover:bg-[#7F070F] focus:outline-none focus:ring-2 focus:ring-[#F68A8E] transition duration-200 ease-in-out transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed'
							>
								{loading ? "Verifying…" : "Verify OTP"}
							</button>
						</form>
					</div>
				</div>
			</div>
		</div>
	);
};

export default OtpPage;
